import {
  Fragment,
  ImgHTMLAttributes,
  useEffect,
  useRef,
  useState,
} from "react";
import useOptimizedImageKey from "./hooks/useOptimizedImageKey";

import { ImagePlaceholderIcon } from "./fancyIcons";
import classNames from "classnames";
import { Transition } from "@headlessui/react";
import { useIsVisible } from "../../hooks/useIsVisible";

const Placeholder = ({ size }: { size: number }) => (
  <div
    className="tw-flex tw-size-full tw-animate-shimmer tw-items-center tw-justify-center tw-bg-gradient-to-r tw-from-neutral-100 tw-via-neutral-50 tw-to-gray-100 tw-bg-[length:200%_100%] tw-fill-neutral-400 tw-stroke-neutral-400"
    style={{ width: `${size}px`, height: `${size}px` }}
  >
    <ImagePlaceholderIcon className="tw-h-1/2 tw-w-1/2" />
  </div>
);

export type OptimizedImageSize = 1024 | 512 | 256 | "original";

export interface OptimizedImageProps
  extends ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  imageSize: OptimizedImageSize;
  getOptimizedSrc: (src: string, size: OptimizedImageSize) => Promise<string>;
  usePlaceholder?: boolean;
  shouldLoadOriginal?: boolean;
}

const OptimizedImage = ({
  src: sourceSrc,
  alt,
  imageSize = 1024,
  getOptimizedSrc = useOptimizedImageKey,
  className,
  usePlaceholder = true,
  shouldLoadOriginal = false,
  ...imgProps
}: OptimizedImageProps) => {
  const [originalSrc, setOriginalSrc] = useState<string | undefined>();
  const [optimizedSrc, setOptimizedSrc] = useState<string | undefined>();
  const [optimizedLoadError, setOptimizedLoadError] = useState<boolean>(false);

  const ref = useRef<HTMLDivElement>(null);
  const isVisible = useIsVisible(ref);

  useEffect(() => {
    (async () => {
      if (isVisible && !optimizedSrc) {
        const newOptimizedSrc = await getOptimizedSrc(sourceSrc, imageSize);

        const optimizedImage = new Image();
        optimizedImage.src = newOptimizedSrc;
        optimizedImage.onload = () => {
          setOptimizedSrc(newOptimizedSrc);
        };

        optimizedImage.onerror = (e) => {
          setOptimizedLoadError(true);
        };
      }
    })();
  }, [getOptimizedSrc, imageSize, isVisible, optimizedSrc, sourceSrc]);

  useEffect(() => {
    (async () => {
      if (
        isVisible &&
        (optimizedLoadError || shouldLoadOriginal) &&
        !originalSrc
      ) {
        const newOriginalSrc = await getOptimizedSrc(sourceSrc, "original");

        const originalImage = new Image();
        originalImage.src = newOriginalSrc;
        originalImage.onload = () => {
          setOriginalSrc(newOriginalSrc);
        };
      }
    })();
  }, [
    getOptimizedSrc,
    imageSize,
    isVisible,
    optimizedLoadError,
    originalSrc,
    shouldLoadOriginal,
    sourceSrc,
  ]);

  return (
    <div
      className={classNames(className, "tw-relative tw-h-full tw-w-full")}
      ref={ref}
    >
      {!(optimizedSrc || originalSrc) && usePlaceholder ? (
        <Placeholder size={imageSize === "original" ? 1024 : imageSize} />
      ) : (
        <Transition
          as={Fragment}
          show={optimizedLoadError || !!optimizedSrc}
          enter="tw-transition tw-ease-out tw-duration-100"
          enterFrom="tw-transform tw-opacity-0 tw-scale-95"
          enterTo="tw-transform tw-opacity-100 tw-scale-100"
          leave="tw-transition tw-ease-in tw-duration-75"
          leaveFrom="tw-transform tw-opacity-100 tw-scale-100"
          leaveTo="tw-transform tw-opacity-0 tw-scale-95"
        >
          <img
            {...imgProps}
            alt={alt}
            src={optimizedLoadError ? originalSrc : optimizedSrc}
            className="tw-size-full tw-object-cover"
          />
        </Transition>
      )}
      <Transition
        as={Fragment}
        show={!!originalSrc}
        enter="tw-transition tw-ease-out tw-duration-100"
        enterFrom="tw-transform tw-opacity-0 tw-scale-95"
        enterTo="tw-transform tw-opacity-100 tw-scale-100"
        leave="tw-transition tw-ease-in tw-duration-75"
        leaveFrom="tw-transform tw-opacity-100 tw-scale-100"
        leaveTo="tw-transform tw-opacity-0 tw-scale-95"
      >
        <img
          {...imgProps}
          alt={alt}
          src={originalSrc}
          className="tw-absolute tw-inset-0 tw-size-full tw-object-cover"
        />
      </Transition>
    </div>
  );
};

export default OptimizedImage;
